/*
 * This file is part of the NSR STEM Utdanning 2022 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useMenu from 'hooks/menu';
import Hamburger from 'components/Hamburger/Hamburger';
import logo from '../../../media/gfx/xfactor-logo-black.svg';
import Menu from './Menu/Menu';
import './XfactorNav.scss';

/**
 * This is the XfactorNav component.
 *
 * @author Thomas Sømoen <thomas.somoen@try.no>
 *
 * @return {JSX}
 */
function XfactorNav({ home, items, languages, stem, labels }) {
  const [buttonRef, menuRef, open, setOpen] = useMenu('menu');

  useEffect(() => {
    if (open) {
      document.documentElement.classList.add('menu-open');
    }

    return () => {
      document.documentElement.classList.remove('menu-open');
    };
  }, [open]);

  function renderLanguages() {
    if (languages.length < 2) return null;

    return languages.map(({ id, short, title, url, active }) => (
      <a key={id} href={url} title={title} className={classNames({ active })}>
        {short}
      </a>
    ));
  }

  return (
    <>
      <nav className="layout-grid main-nav">
        <button
          ref={buttonRef}
          id="menubutton"
          className="toggle"
          aria-label={open ? labels.close : labels.open}
          aria-haspopup="true"
          aria-controls="menu"
          aria-expanded={open ? 'true' : 'false'}
        >
          <Hamburger open={open} />
        </button>
        <a className="home" href={home.url} title={home.title}>
          <img src={logo} width="205" height="32" alt={home.alt} />
        </a>

        <div className="languages">{renderLanguages()}</div>
      </nav>
      <Menu
        ref={menuRef}
        languages={languages}
        items={items}
        stem={stem}
        home={home}
        open={open}
        labels={labels}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
XfactorNav.propTypes = {
  home: PropTypes.shape({
    url: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    alt: PropTypes.string,
  }).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      active: PropTypes.bool,
    })
  ).isRequired,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      short: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  search: PropTypes.shape({
    action: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  stem: PropTypes.shape({
    url: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  labels: PropTypes.shape({
    open: PropTypes.string.isRequired,
    close: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
XfactorNav.defaultProps = {
  home: {
    url: '/',
    title: 'To frontpage',
    alt: '',
  },
  items: [],
  languages: [],
  search: {
    action: '/search',
    label: 'Search',
    button: 'Search',
    placeholder: 'Enter your search',
    name: 'q',
  },
  stem: {
    url: '',
    text: '',
    title: '',
  },
  labels: {
    open: 'Open menu',
    close: 'Open menu',
  },
};

export default XfactorNav;
